import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { getAuthToken } from "../../../utils/getAuthToken";

// API base URL with fallback
const SERVER_DOMAIN = process.env.REACT_APP_SERVER_DOMAIN || 'http://localhost:8000';

const postReportsJson = async (path, body) => {
  const token = await getAuthToken();
  if (!token) {
    throw new Error("No token found");
  }

  const response = await fetch(`${SERVER_DOMAIN}/api/reports/${path}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error || errorData.message || "Request failed");
  }

  return response.json();
};

// Fetches Completed/Partial/Failed broadcast counts (see reportController.js's
// getBroadcastsOverview) for the Broadcasts Overview report, broken down per
// day so the client can bucket/group them for its chart. `filters` (campaignIds/
// sentBy/datasetIds/labels) mirror the report page's shared filter bar.
export const fetchBroadcastsOverview = createAsyncThunk(
  "broadcastsReport/fetchOverview",
  async ({ startDate, endDate, filters }, { rejectWithValue }) => {
    try {
      return await postReportsJson("getbroadcastsoverview", { startDate, endDate, ...filters });
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Fetches chat-reply/quote-reply counts per day (see reportController.js's
// getBroadcastRepliesOverview) for the Template Replies chart. Shares the same
// date range + filter bar as fetchBroadcastsOverview.
export const fetchBroadcastRepliesOverview = createAsyncThunk(
  "broadcastsReport/fetchReplies",
  async ({ startDate, endDate, filters }, { rejectWithValue }) => {
    try {
      return await postReportsJson("getbroadcastrepliesoverview", { startDate, endDate, ...filters });
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Fetches Sent/Delivered/Failed/Read counts (see reportController.js's
// getBroadcastMessageStatusOverview) for the Message Status chart, broken down per day so
// the client can bucket/group them for its chart. Shares the same date range + filter bar
// as fetchBroadcastsOverview.
export const fetchBroadcastMessageStatusOverview = createAsyncThunk(
  "broadcastsReport/fetchMessageStatus",
  async ({ startDate, endDate, filters }, { rejectWithValue }) => {
    try {
      return await postReportsJson("getbroadcastmessagestatusoverview", { startDate, endDate, ...filters });
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Fetches one page of the Failed Messages table (see reportController.js's
// getBroadcastFailedMessages) - contact name/number, broadcast name, and failure reason for
// each failed send within the date range. Shares the same date range + filter bar as the charts
// above; `page`/`limit` drive server-side pagination since a wide range can have thousands of rows.
export const fetchBroadcastFailedMessages = createAsyncThunk(
  "broadcastsReport/fetchFailedMessages",
  async ({ startDate, endDate, filters, page, limit }, { rejectWithValue }) => {
    try {
      return await postReportsJson("getbroadcastfailedmessages", { startDate, endDate, page, limit, ...filters });
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Type-ahead search for one filter-bar dropdown's options (see reportController.js's
// getBroadcastFilterOptions). `field` is one of broadcast/sentBy/dataset/label.
export const fetchBroadcastFilterOptions = createAsyncThunk(
  "broadcastsReport/fetchFilterOptions",
  async ({ field, search }, { rejectWithValue }) => {
    try {
      const data = await postReportsJson("getbroadcastfilteroptions", { field, search });
      return { field, options: data.options || [] };
    } catch (error) {
      return rejectWithValue({ field, message: error.message });
    }
  }
);

const emptyFilterOptionState = { items: [], loading: false, error: null };

const broadcastsReportSlice = createSlice({
  name: "broadcastsReport",
  initialState: {
    totalCompleted: 0,
    totalPartial: 0,
    totalFailed: 0,
    days: [],
    loading: false,
    error: null,
    replies: {
      totalChatReplies: 0,
      totalQuoteReplies: 0,
      days: [],
      loading: false,
      error: null,
    },
    messageStatus: {
      totalSent: 0,
      totalDelivered: 0,
      totalFailed: 0,
      totalRead: 0,
      days: [],
      loading: false,
      error: null,
    },
    filterOptions: {
      broadcast: { ...emptyFilterOptionState },
      sentBy: { ...emptyFilterOptionState },
      dataset: { ...emptyFilterOptionState },
      label: { ...emptyFilterOptionState },
    },
    failedMessages: {
      rows: [],
      total: 0,
      page: 1,
      limit: 25,
      loading: false,
      error: null,
    },
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchBroadcastsOverview.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBroadcastsOverview.fulfilled, (state, action) => {
        state.loading = false;
        state.totalCompleted = action.payload.totalCompleted || 0;
        state.totalPartial = action.payload.totalPartial || 0;
        state.totalFailed = action.payload.totalFailed || 0;
        state.days = Array.isArray(action.payload.days) ? action.payload.days : [];
        state.error = null;
      })
      .addCase(fetchBroadcastsOverview.rejected, (state, action) => {
        state.loading = false;
        state.totalCompleted = 0;
        state.totalPartial = 0;
        state.totalFailed = 0;
        state.days = [];
        state.error = action.payload;
      })

      .addCase(fetchBroadcastRepliesOverview.pending, (state) => {
        state.replies.loading = true;
        state.replies.error = null;
      })
      .addCase(fetchBroadcastRepliesOverview.fulfilled, (state, action) => {
        state.replies.loading = false;
        state.replies.totalChatReplies = action.payload.totalChatReplies || 0;
        state.replies.totalQuoteReplies = action.payload.totalQuoteReplies || 0;
        state.replies.days = Array.isArray(action.payload.days) ? action.payload.days : [];
        state.replies.error = null;
      })
      .addCase(fetchBroadcastRepliesOverview.rejected, (state, action) => {
        state.replies.loading = false;
        state.replies.totalChatReplies = 0;
        state.replies.totalQuoteReplies = 0;
        state.replies.days = [];
        state.replies.error = action.payload;
      })

      .addCase(fetchBroadcastMessageStatusOverview.pending, (state) => {
        state.messageStatus.loading = true;
        state.messageStatus.error = null;
      })
      .addCase(fetchBroadcastMessageStatusOverview.fulfilled, (state, action) => {
        state.messageStatus.loading = false;
        state.messageStatus.totalSent = action.payload.totalSent || 0;
        state.messageStatus.totalDelivered = action.payload.totalDelivered || 0;
        state.messageStatus.totalFailed = action.payload.totalFailed || 0;
        state.messageStatus.totalRead = action.payload.totalRead || 0;
        state.messageStatus.days = Array.isArray(action.payload.days) ? action.payload.days : [];
        state.messageStatus.error = null;
      })
      .addCase(fetchBroadcastMessageStatusOverview.rejected, (state, action) => {
        state.messageStatus.loading = false;
        state.messageStatus.totalSent = 0;
        state.messageStatus.totalDelivered = 0;
        state.messageStatus.totalFailed = 0;
        state.messageStatus.totalRead = 0;
        state.messageStatus.days = [];
        state.messageStatus.error = action.payload;
      })

      .addCase(fetchBroadcastFailedMessages.pending, (state) => {
        state.failedMessages.loading = true;
        state.failedMessages.error = null;
      })
      .addCase(fetchBroadcastFailedMessages.fulfilled, (state, action) => {
        state.failedMessages.loading = false;
        state.failedMessages.rows = Array.isArray(action.payload.rows) ? action.payload.rows : [];
        state.failedMessages.total = action.payload.total || 0;
        state.failedMessages.page = action.payload.page || 1;
        state.failedMessages.limit = action.payload.limit || 25;
        state.failedMessages.error = null;
      })
      .addCase(fetchBroadcastFailedMessages.rejected, (state, action) => {
        state.failedMessages.loading = false;
        state.failedMessages.rows = [];
        state.failedMessages.total = 0;
        state.failedMessages.error = action.payload;
      })

      .addCase(fetchBroadcastFilterOptions.pending, (state, action) => {
        const { field } = action.meta.arg;
        state.filterOptions[field].loading = true;
        state.filterOptions[field].error = null;
      })
      .addCase(fetchBroadcastFilterOptions.fulfilled, (state, action) => {
        const { field, options } = action.payload;
        state.filterOptions[field].loading = false;
        state.filterOptions[field].items = options;
      })
      .addCase(fetchBroadcastFilterOptions.rejected, (state, action) => {
        const { field, message } = action.payload || {};
        if (!field) return;
        state.filterOptions[field].loading = false;
        state.filterOptions[field].items = [];
        state.filterOptions[field].error = message;
      });
  },
});

export default broadcastsReportSlice.reducer;
